import { Link } from 'react-router-dom';
import { timeAgo, formatDateTime } from '@/lib/time.js';
import { EmptyState } from '@/components/PageState.js';
import { StatusBadge } from '@/components/StatusBadge.js';
import { PriorityIcon } from '@/components/PriorityIcon.js';
import { Avatar } from '@/components/Avatar.js';
import { LinkIndicator } from '@/components/LinkIndicator.js';
import { IssueTypeIcon } from '@/components/IssueTypeIcon.js';
import { EditableStatusCell, EditablePriorityCell, EditableAssigneeCell } from '@/components/InlineEditCells.js';
import type { JiraIssueSummary } from '@shared/types.js';

interface IssueListProps {
  issues: JiraIssueSummary[];
  sortField?: string;
  sortDir?: 'ASC' | 'DESC';
  onSort?: (field: string) => void;
  editable?: boolean;
  priorities?: Array<{ id: string; name: string }>;
  users?: Array<{ accountId: string; displayName: string; avatarUrl: string | null }>;
}


type Align = 'left' | 'center' | 'right';

interface ColumnDef {
  field: string;
  label: string;
  align: Align;
  width?: string;
  hideClass?: string;
}

const COLUMNS: ColumnDef[] = [
  { field: 'key', label: 'Key', align: 'left', width: 'w-28 sm:w-32' },
  { field: 'summary', label: 'Summary', align: 'left' },
  { field: 'status', label: 'Status', align: 'left', width: 'w-36', hideClass: 'hidden sm:table-cell' },
  { field: 'priority', label: 'Priority', align: 'center', width: 'w-20', hideClass: 'hidden md:table-cell' },
  { field: 'assignee', label: 'Assignee', align: 'left', width: 'w-44', hideClass: 'hidden lg:table-cell' },
  { field: 'reporter', label: 'Reporter', align: 'left', width: 'w-44', hideClass: 'hidden xl:table-cell' },
  { field: 'updated', label: 'Updated', align: 'right', width: 'w-24', hideClass: 'hidden sm:table-cell' },
];

function alignClass(align: Align): string {
  if (align === 'center') return 'text-center';
  return align === 'right' ? 'text-right' : 'text-left';
}

interface SortableHeaderProps {
  col: ColumnDef;
  currentSort?: string;
  currentDir?: 'ASC' | 'DESC';
  onSort?: (field: string) => void;
}

function SortableHeader({ col, currentSort, currentDir, onSort }: SortableHeaderProps) {
  const isActive = currentSort === col.field;
  const arrow = isActive ? (currentDir === 'ASC' ? ' \u2191' : ' \u2193') : '';
  const cls = `px-3 py-2 text-xs font-semibold ${alignClass(col.align)} ${col.width || ''} ${col.hideClass || ''}`;

  if (!onSort || col.field === 'reporter') {
    return <th className={`${cls} text-gray-500`}>{col.label}</th>;
  }

  return (
    <th className={cls} aria-sort={isActive ? (currentDir === 'ASC' ? 'ascending' : 'descending') : undefined}>
      <button
        onClick={() => onSort(col.field)}
        className={`whitespace-nowrap hover:text-gray-900 ${isActive ? 'text-blue-700' : 'text-gray-500'}`}
      >
        {col.label}{arrow}
      </button>
    </th>
  );
}

export function IssueList({ issues, sortField, sortDir, onSort, editable, priorities, users }: IssueListProps) {
  if (issues.length === 0) {
    return <EmptyState title="No issues found">Try a different filter or search.</EmptyState>;
  }

  return (
    <div className="rounded-lg border bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50/80">
            {COLUMNS.map((col) => (
              <SortableHeader key={col.field} col={col} currentSort={sortField} currentDir={sortDir} onSort={onSort} />
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {issues.map((issue) => {
            const isDone = issue.status.category === 'done';
            return (
              <tr key={issue.key} className="group hover:bg-blue-50/40">
                <td className="whitespace-nowrap px-3 py-2">
                  <div className="flex items-center gap-2">
                    <IssueTypeIcon name={issue.issuetype.name} />
                    <Link to={`/issues/${issue.key}`} className={`font-medium text-blue-600 hover:underline ${isDone ? 'text-gray-500 line-through' : ''}`}>
                      {issue.key}
                    </Link>
                    <a
                      href={`https://weblisters.atlassian.net/browse/${issue.key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 opacity-0 transition-opacity hover:text-blue-500 group-hover:opacity-100"
                      title="Open in Jira"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </td>
                <td className="max-w-0 px-3 py-2">
                  <div className="flex items-center gap-2">
                    {issue.parent && (
                      <Link
                        to={`/issues/${issue.parent.key}`}
                        className="shrink-0 rounded bg-gray-100 px-1.5 py-px text-[11px] font-medium text-gray-600 hover:bg-gray-200 hover:text-blue-700"
                        title={`Under ${issue.parent.key}: ${issue.parent.summary}`}
                      >
                        {issue.parent.key}
                      </Link>
                    )}
                    <Link to={`/issues/${issue.key}`} className="truncate text-gray-900 hover:text-blue-700" title={issue.summary}>
                      {issue.summary}
                    </Link>
                    <LinkIndicator links={issue.links} />
                  </div>
                </td>
                <td className="hidden px-3 py-2 sm:table-cell">
                  {editable ? (
                    <EditableStatusCell issue={issue} />
                  ) : (
                    <StatusBadge status={issue.status} />
                  )}
                </td>
                <td className="hidden px-3 py-2 text-center md:table-cell">
                  {editable && priorities ? (
                    <EditablePriorityCell issue={issue} priorities={priorities} />
                  ) : (
                    <div className="flex items-center justify-center">
                      <PriorityIcon name={issue.priority.name} iconUrl={issue.priority.iconUrl} />
                    </div>
                  )}
                </td>
                <td className="hidden px-3 py-2 lg:table-cell">
                  {editable && users ? (
                    <EditableAssigneeCell issue={issue} users={users} />
                  ) : issue.assignee ? (
                    <div className="flex min-w-0 items-center gap-2">
                      <Avatar name={issue.assignee.displayName} avatarUrl={issue.assignee.avatarUrl} />
                      <span className="truncate text-gray-700">{issue.assignee.displayName}</span>
                    </div>
                  ) : (
                    <span className="text-gray-400">Unassigned</span>
                  )}
                </td>
                <td className="hidden max-w-44 px-3 py-2 xl:table-cell">
                  {issue.reporter ? (
                    <div className="flex items-center gap-2" title={`Reporter: ${issue.reporter.displayName}`}>
                      <Avatar name={issue.reporter.displayName} avatarUrl={issue.reporter.avatarUrl} />
                      <span className="truncate text-gray-700">{issue.reporter.displayName}</span>
                    </div>
                  ) : <span className="text-gray-400">—</span>}
                </td>
                <td className="hidden whitespace-nowrap px-3 py-2 text-right text-gray-500 sm:table-cell" title={formatDateTime(issue.updated)}>
                  {timeAgo(issue.updated)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
